import { MessageType, NcqLogLevel, TimeoutDuration } from "../enums.generated";

/**
 * Client context information interface. Holds contextual information about the client state and configuration.
 */
export interface ClientContext {
	pathBase: string;
	dateFormat: string;
	userId: number;
	userTier: CurrentUserSelectedTier;
	content: { [key: string]: string; };
	sessionExpiry?: SessionExpiryInfo;
	clientDebugEnabled: boolean;
	clientLoggingDisabled: boolean;
}

/**
 * Group / tier 3 interface.
 */
export interface Group extends Tier {

	/**
	 * Group / tier 3 label.
	 */
	groupLabel: string;

	/**
	 * Branch / tier 2 label.
	 */
	branchLabel: string;

	/**
	 * Broker / tier 3 label.
	 */
	brokerLabel: string;
}

/**
 * Base message interface.
 */
export interface MessageBase {

	/** 
	 * Message title.
	 */
	title: string;

	/**
	 * Message text.
	 */
	text: string;

	/**
	 * Message type.
	 */
	type?: MessageType;
}

/**
 * Message interface.
 * @see MessageBase
 */
export interface Message extends MessageBase {

	/**
	 * Message element ID.
	 */
	id?: string;

	/**
	 * Is the message dismissible?
	 */
	dismissible?: boolean;

	/**
	 * Function that dismisses a message.
	 */
	dismiss?: (message: Message) => void;

	/**
	 * Timeout for displaying the message. Once the timeout has elapsed the message will be hidden.
	 */
	timeoutDuration?: TimeoutDuration;

	/**
	 * The current timeout (if any) for the message that is about to timeout.
	 */
	timeout?: NodeJS.Timeout;
}

/**
 * Selected tier interface.
 */
export interface SelectedTier {

	/**
	 * Group / tier 1.
	 */
	group?: Group;

	/**
	 * Branch / tier 2.
	 */
	branch?: Tier;

	/**
	 * Broker / tier 3.
	 */
	broker?: Tier;
}

/** 
 * Current users selected tier interface.
 * @see MessageBase
 */
export interface CurrentUserSelectedTier extends SelectedTier {

	/**
	 * The current users ID.
	 */
	userId: number;
}

/**
 * Tier interface.
 */
export interface Tier {

	/**
	 * Tier ID string.
	 */
	id: string;

	/**
	 * Tier name string.
	 */
	name: string;

	/**
	 * Tier path string.
	 */
	path: string;
}

/**
 * Ensure tier level enumeration. Used to denote which level of tier to ensure.
 */
export enum EnsureTierLevel {

	/**
	 * Don't ensure any tier.
	 */
	None,

	/**
	 * Ensure group / tier 1.
	 */
	Group,

	/**
	 * Ensure broker / tier 3.
	 */
	Broker
}

/**
 * Font based icon interface.
 */
export interface Icon {

	/**
	 * Font class name.
	 */
	className: string;

	/**
	 * Icon tooltip, if any.
	 */
	tooltip: string;
}

/**
 * Confirmation prompt options interface.
 * @see MessageBase 
 */
export interface ConfirmationPromptOptions extends MessageBase {

	/**
	 * Auto open the prompt, default is true.
	 */
	autoOpen?: boolean;

	/**
	 * Function to open the prompt.
	 */
	open?: () => void;

	/**
	 * Function to close the prompt.
	 */
	close?: () => void;

	/**
	 * Function to refresh the prompt.
	 */
	refresh?: () => void;

	/**
	 * The confirm button options.
	 */
	confirmOption?: ConfirmationOption;

	/**
	 * The cancel button options.
	 */
	cancelOption?: ConfirmationOption;

	/**
	 * Flag to reverse the order buttons are displayed.
	 */
	reverseButtons?: boolean;

	/**
	 * Content element to display within the prompt.
	 */
	content?: HTMLElement;

	/**
	 * Flag to close after transitions have completed.
	 */
	closeAfterTransition?: boolean;

	/**
	 * On prompt open handler function, called after the prompt has been opened.
	 */
	onOpen?: () => void;

	/**
	 * On prompt rendered handler function, called after the prompt is rendered for display.
	 */
	onRendered?: (isOpen?: boolean) => void;

	/**
	 * Array of messages to display within the prompt.
	 */
	messages?: Array<Message>;

	/**
	 * Flag to render message text as HTML.
	 */
	renderTextAsHtml?: boolean;

	/** 
	 * Target element ID for the prompt, this dictates which element the prompt is added to. 
	 * This is important for adding prompts which call page handlers.
	 */
	targetElementId?: string;

	/**
	 * Flag to close prompt on click of the modal background.
	 */
	closeOnBackdropClick?: boolean;
}

/**
 *  Confirmation option interface, defines properties required to display prompt buttons.
 */
export interface ConfirmationOption {

	/**
	 * Navigate URL for the button.
	 */
	actionUrl: string | undefined;

	/**
	 * Page handler for the button.
	 */
	pageHandler: string | undefined;

	/**
	 * Button style.
	 */
	style: "primary" | "secondary";

	/**
	 * Button text displayed.
	 */
	text: string;

	/**
	 * Function handler for button clicks.
	 */
	handler?: () => void;
}

/** Session expiry info interface, defines properties required to display the session expiry prompt. */
export interface SessionExpiryInfo {

	/**
	 * Absolute session expiry time in seconds. 
	 * When this number of seconds expires the user will be logged out. 
	 * It is the maximum session duration in seconds. 
	 */
	absoluteSessionExpirySeconds?: number;

	/** 
	 *  Session expiry in seconds.
	 *  This is a rolling expiry that is refreshed when the server is next called.
	 */
	sessionExpirySeconds?: number;

	/**
	 * Flag to determine whether the session absolutely expires at midnight.
	 */
	sessionExpiryAtMidnight: boolean;

	/**
	 * Number of seconds left on the session until the session expiry warning prompt should be displayed.
	 */
	sessionExpiryWarningSeconds?: number;

	/**
	 * Session length in minutes.
	 */
	sessionTimeout: number;
}

/**
 * Log message interface, used for logging client-side messages to the server.
 */
export interface LogMessage {

	/**
	 * Log type string.
	 */
	logType: string;

	/**
	 * Log message string.
	 */
	message: string;

	/**
	 * Log details string.
	 */
	detail: string;

	/**
	 * Log level.
	 */
	level: NcqLogLevel;
}